import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, Bell, CheckCircle2, AlertCircle, Info, Trash2, ExternalLink, Mail, Zap, Target, ShieldCheck } from 'lucide-react';

const formatTimeAgo = (dateString, t) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);
  
  if (diffInSeconds < 60) return `${t('notifications.time_ago')} ${diffInSeconds}${t('notifications.time_s')}`;
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `${t('notifications.time_ago')} ${diffInMinutes}${t('notifications.time_m')}`;
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${t('notifications.time_ago')} ${diffInHours}${t('notifications.time_h')}`;
  const diffInDays = Math.floor(diffInHours / 24);
  return `${t('notifications.time_ago')} ${diffInDays}${t('notifications.time_d')}`;
};


export default function NotificationDrawer({ isOpen, onClose, notifications, unreadCount, markAsRead, markAllAsRead, clearAll, onNotificationClick }) {
  const { t } = useTranslation();
  if (!isOpen) return null;

  const handleNotificationClick = (notif) => {
    if (!notif.is_read) {
      markAsRead(notif.id);
    }
    if (onNotificationClick) {
      onNotificationClick(notif);
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'deposit':
      case 'success':
        return <div className="p-2 bg-emerald-500/10 rounded-lg border border-emerald-500/20"><CheckCircle2 className="text-emerald-400" size={18} /></div>;
      case 'withdrawal':
      case 'error':
        return <div className="p-2 bg-rose-500/10 rounded-lg border border-rose-500/20"><AlertCircle className="text-rose-400" size={18} /></div>;
      case 'trade':
        return <div className="p-2 bg-indigo-500/10 rounded-lg border border-indigo-500/20"><Zap className="text-indigo-400" size={18} /></div>;
      case 'team':
        return <div className="p-2 bg-cyan-500/10 rounded-lg border border-cyan-500/20"><Target className="text-cyan-400" size={18} /></div>;
      case 'kyc':
        return <div className="p-2 bg-amber-500/10 rounded-lg border border-amber-500/20"><ShieldCheck className="text-amber-400" size={18} /></div>;
      case 'broadcast':
        return <div className="p-2 bg-purple-500/10 rounded-lg border border-purple-500/20"><Mail className="text-purple-400" size={18} /></div>;
      default:
        return <div className="p-2 bg-slate-500/10 rounded-lg border border-slate-500/20"><Bell className="text-slate-400" size={18} /></div>;
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[99999] overflow-hidden">
      {/* Overlay avec flou progressif */}
      <div 
        className="absolute inset-0 bg-slate-950/40 backdrop-blur-md transition-opacity duration-500 ease-out animate-in fade-in" 
        onClick={onClose}
      />
      
      <div className="absolute inset-y-0 right-0 max-w-full flex">
        <div className="w-screen max-w-md animate-in slide-in-from-right duration-500 ease-out">
          <div className="h-full flex flex-col bg-[#0f172a]/90 backdrop-blur-2xl border-l border-white/10 shadow-[0_0_50px_rgba(0,0,0,0.5)] overflow-hidden">
            
            {/* Header Premium */}
            <div className="px-8 py-8 border-b border-white/5 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 blur-3xl rounded-full -mr-16 -mt-16 pointer-events-none" />
              
              <div className="flex items-center justify-between mb-6 relative z-10">
                <div className="flex items-center gap-4">
                    <div className="p-3 bg-indigo-500/10 rounded-2xl border border-indigo-500/20">
                        <Bell className="text-indigo-400" size={24} />
                    </div>
                    <div>
                        <h2 className="text-2xl font-black text-white tracking-tighter uppercase leading-none">
                          {t('notifications.title')}
                        </h2>
                        <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mt-1">
                            {t('notifications.unread_count', { count: unreadCount })}
                        </p>
                    </div>
                </div>
                <button 
                  onClick={onClose}
                  className="p-3 rounded-2xl bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-all border border-white/10"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="flex items-center justify-between relative z-10">
                <button 
                  onClick={markAllAsRead}
                  className="group flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-500/10 border border-indigo-500/20 text-[10px] font-black text-indigo-400 uppercase tracking-widest hover:bg-indigo-500 hover:text-white transition-all"
                >
                  <CheckCircle2 size={14} className="group-hover:scale-110 transition-transform" />
                  {t('notifications.mark_all_read')}
                </button>
                <button 
                  onClick={clearAll}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-[10px] font-black text-slate-500 uppercase tracking-widest hover:text-rose-400 hover:border-rose-400/20 hover:bg-rose-500/10 transition-all"
                >
                  <Trash2 size={14} />
                  {t('notifications.clean')}
                </button>
              </div>
            </div>

            {/* List avec Scrollbar Custom */}
            <div className="flex-1 px-6 py-6 space-y-4 overflow-y-auto scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
              {notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center space-y-6 opacity-40">
                  <div className="p-8 rounded-full bg-white/5 border border-white/5 animate-pulse">
                    <Bell size={64} className="text-slate-500" />
                  </div>
                  <div>
                    <p className="text-lg font-black text-white uppercase tracking-tighter">{t('notifications.empty')}</p>
                    <p className="text-sm text-slate-500 font-medium max-w-[200px] mx-auto">{t('notifications.empty_desc')}</p>
                  </div>
                </div>
              ) : (
                notifications.map((notif) => (
                  <div 
                    key={notif.id}
                    className={`relative p-5 rounded-[2rem] border transition-all duration-300 cursor-pointer group overflow-hidden ${
                      !notif.is_read 
                        ? 'bg-indigo-500/10 border-indigo-500/30 hover:bg-indigo-500/15' 
                        : 'bg-white/5 border-white/5 hover:bg-white/10'
                    }`}
                    onClick={() => handleNotificationClick(notif)}
                  >
                    {/* Indicateur de lecture stylisé */}
                    {!notif.is_read && (
                      <div className="absolute top-0 right-0 w-24 h-24 bg-indigo-500/20 blur-[40px] pointer-events-none" />
                    )}
                    
                    <div className="flex gap-5 relative z-10">
                      <div className="flex-shrink-0">
                        {getIcon(notif.type)}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                            <span className={`text-[9px] font-black uppercase tracking-widest ${
                                !notif.is_read ? 'text-indigo-400' : 'text-slate-600'
                            }`}>
                                {notif.type || 'info'}
                            </span>
                            <span className="text-[10px] text-slate-500 font-bold">
                                {formatTimeAgo(notif.created_at, t)}
                            </span>
                        </div>
                        <h4 className="text-base font-black text-white leading-tight mb-2 tracking-tight group-hover:text-indigo-300 transition-colors">
                          {t(notif.title, { amount: notif.data?.amount, asset: notif.data?.asset })}
                        </h4>
                        <p className={`text-sm leading-relaxed ${
                            !notif.is_read ? 'text-slate-300 font-medium' : 'text-slate-500 font-normal'
                        }`}>
                          {t(notif.message, { amount: notif.data?.amount, asset: notif.data?.asset })}
                        </p>
                        
                        {notif.data && (
                            <div className="mt-4 flex items-center justify-end">
                                <button className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-white hover:bg-indigo-500/20 transition-all">
                                    {t('notifications.view_details')}
                                    <ExternalLink size={10} />
                                </button>
                            </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            
            {/* Footer Glassmorphism */}
            <div className="p-8 border-t border-white/5 bg-black/20 backdrop-blur-xl">
              <button 
                onClick={onClose}
                className="w-full py-4 rounded-[1.5rem] bg-indigo-500 hover:bg-indigo-400 text-white text-xs font-black uppercase tracking-[0.2em] transition-all shadow-xl shadow-indigo-500/20 active:scale-95"
              >
                {t('notifications.close_all')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
